import {
    CustomListItemId,
    PlaybookId,
    PlaybookTouchpointId,
    RegionId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { TierTouchpointsEditor } from "components/manage/agency-settings/tier-touchpoints-editor";
import {
    EditableTouchpoint,
    groupTouchpointsByTier,
} from "components/manage/agency-settings/touchpoint-cadence";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { describeCadence, describeChannels } from "./marketing-plan-format";
import { useAgendaRefresh } from "./use-agenda-refresh";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];
type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["myPlaybook"]
>["touchpoints"][number];

const summarizeTouchpoint = (touchpoint: Touchpoint): string =>
    `${describeChannels(touchpoint.kinds)} ${describeCadence(
        touchpoint.intervalCount,
        touchpoint.intervalUnit,
    )}`;

export const MyMarketingPlanEditor: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const visitsEnabled = useFeature("visits");
    const regionId = useRegionId();
    const [newRows, setNewRows] = React.useState<
        { key: string; tierListItemId: CustomListItemId; initial: EditableTouchpoint }[]
    >([]);
    const newRowCounter = React.useRef(0);

    const tiersQuery = useQuery(trpcClient.referralTier.tiers.queryOptions());
    const myPlaybookQuery = useQuery({
        ...trpcClient.marketingPlaybook.myPlaybook.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const refreshAgenda = useAgendaRefresh();

    const refetch = () => {
        myPlaybookQuery.refetch();
        refreshAgenda();
    };

    const customize = useMutation({
        ...trpcClient.marketingPlaybook.customizePlaybook.mutationOptions(),
        onSuccess: refetch,
    });
    const reset = useMutation({
        ...trpcClient.marketingPlaybook.resetPlaybook.mutationOptions(),
        onSuccess: () => {
            setNewRows([]);
            refetch();
        },
    });
    const createTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.createTouchpoint.mutationOptions(),
        onSuccess: refetch,
    });
    const updateTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.updateTouchpoint.mutationOptions(),
        onSuccess: refetch,
    });
    const deleteTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.deleteTouchpoint.mutationOptions(),
        onSuccess: refetch,
    });

    if (!marketingPlaybookEnabled || !regionId) {
        return null;
    }

    const view = myPlaybookQuery.data;
    const isPersonal = view?.isPersonal ?? false;
    const hasAgencyPlaybook = view?.hasAgencyPlaybook ?? false;
    const playbookId = view?.playbookId as PlaybookId | undefined;
    const tiers: Tier[] = (tiersQuery.data ?? []).filter(tier => !tier.isArchived);
    const touchpointsByTier = groupTouchpointsByTier(view?.touchpoints ?? []);

    const addRow = (tierListItemId: CustomListItemId, initial: EditableTouchpoint) => {
        newRowCounter.current += 1;
        setNewRows(prev => [
            ...prev,
            { key: `new-${newRowCounter.current}`, tierListItemId, initial },
        ]);
    };

    const cancelNewRow = (key: string) => {
        setNewRows(prev => prev.filter(row => row.key !== key));
    };

    const createTouchpointFromRow = (key: string, value: EditableTouchpoint) => {
        const row = newRows.find(r => r.key === key);
        if (!row || !playbookId || value.kinds.length === 0) {
            return;
        }
        handleEvent("marketing-plan-touchpoint-create");
        createTouchpoint.mutate(
            {
                regionId,
                playbookId,
                tierListItemId: row.tierListItemId,
                kinds: value.kinds,
                intervalUnit: value.intervalUnit,
                intervalCount: value.intervalCount,
            },
            { onSuccess: () => cancelNewRow(key) },
        );
    };

    const saveTouchpoint = (id: string, value: EditableTouchpoint) => {
        if (value.kinds.length === 0) {
            return;
        }
        handleEvent("marketing-plan-touchpoint-update");
        updateTouchpoint.mutate({
            regionId,
            id: id as PlaybookTouchpointId,
            kinds: value.kinds,
            intervalUnit: value.intervalUnit,
            intervalCount: value.intervalCount,
        });
    };

    const deleteTouchpointById = (id: string) => {
        handleEvent("marketing-plan-touchpoint-delete");
        deleteTouchpoint.mutate({ regionId, id: id as PlaybookTouchpointId });
    };

    const startCustomizing = () => {
        handleEvent("marketing-plan-customize");
        customize.mutate({ regionId });
    };

    const revertToAgencyPlan = async () => {
        const confirmed = await fancyConfirm(
            "Revert to agency plan?",
            "This discards your personalized touchpoints and puts you back on your agency's marketing plan.",
            "Yes, revert",
            "Cancel",
        );
        if (!confirmed) {
            return;
        }
        handleEvent("marketing-plan-revert");
        reset.mutate({ regionId });
    };

    if (myPlaybookQuery.isLoading) {
        return (
            <div className="flex justify-center rounded-lg bg-white p-8 ring-1 ring-gray-300">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="space-y-4 rounded-lg bg-white p-4 ring-1 ring-gray-300">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600">
                    {isPersonal
                        ? "You've personalized your marketing plan. These cadences drive your agenda."
                        : hasAgencyPlaybook
                          ? "You're following your agency's marketing plan."
                          : "Your agency hasn't set up a marketing plan yet."}
                </p>
                {isPersonal ? (
                    <SecondaryButton onClick={revertToAgencyPlan}>
                        Revert to agency plan
                    </SecondaryButton>
                ) : (
                    <PrimaryButton onClick={startCustomizing}>
                        {hasAgencyPlaybook ? "Customize my plan" : "Create my plan"}
                    </PrimaryButton>
                )}
            </div>
            <div className="space-y-3">
                {tiers.map(tier => {
                    const tierItemId = tier.id as CustomListItemId;
                    const tierTouchpoints = touchpointsByTier.get(tier.id) ?? [];
                    return (
                        <div
                            key={tier.id}
                            className="space-y-3 rounded-lg border border-gray-200 p-4">
                            <div className="text-sm font-medium text-gray-800">
                                {tier.name || "Tier"}
                            </div>
                            {isPersonal ? (
                                <TierTouchpointsEditor
                                    touchpoints={tierTouchpoints}
                                    newRows={newRows
                                        .filter(row => row.tierListItemId === tier.id)
                                        .map(row => ({
                                            key: row.key,
                                            initial: row.initial,
                                        }))}
                                    tierKey={tier.key}
                                    visitsEnabled={visitsEnabled}
                                    onAddRow={initial => addRow(tierItemId, initial)}
                                    onSaveTouchpoint={saveTouchpoint}
                                    onDeleteTouchpoint={deleteTouchpointById}
                                    onCreateTouchpoint={createTouchpointFromRow}
                                    onCancelNewRow={cancelNewRow}
                                />
                            ) : tierTouchpoints.length === 0 ? (
                                <div className="text-sm text-gray-400">
                                    No touchpoints scheduled.
                                </div>
                            ) : (
                                <ul className="space-y-1">
                                    {tierTouchpoints.map(touchpoint => (
                                        <li
                                            key={touchpoint.id}
                                            className="text-sm text-gray-700">
                                            {summarizeTouchpoint(touchpoint)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
